import {
  FAKE_COMMENT_FILE_ACTION,
  FAKE_COMMENT_MESSAGE_KIND,
} from "@/components/ui/Cfakecomment";
import type { FakeCommentFileAction, FakeCommentFileAttachment, FakeCommentMessage } from "@/components/ui/Cfakecomment";
import { Provider } from "@cabal/shared/domain/llm/model/enums.js";
import { TeamRole } from "@cabal/shared/domain/session/team/enums.js";

// ---------------------------------------------------------------------------
// Shared comment shape
// ---------------------------------------------------------------------------

export interface ScrollCommentData {
  name: string;
  model?: string;
  role: TeamRole;
  teamRole?: string | null;
  message: FakeCommentMessage;
  date: string;
  colorHex?: string;
  provider?: string;
  animationDelay?: number;
  animationDuration?: number;
  bubbleRadius?: string | number;
}

// ---------------------------------------------------------------------------
// Return type for getTabContent
// ---------------------------------------------------------------------------

export interface ChatTeaserTabContent {
  /** Single-AI solo-panel comment, fully constructed for the given tab. */
  solo: ScrollCommentData;
  /** Multi-model conversation thread for the given tab. */
  united: readonly ScrollCommentData[];
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface AgentPreset {
  provider: string | undefined;
  model: string;
  colorHex: string;
}

const AGENT_PRESETS = {
  Claude:     { provider: Provider.Anthropic,  model: "Sonnet",  colorHex: "#8B5CF6" },
  GPT:        { provider: Provider.OpenAI,     model: "GPT-5",   colorHex: "#10B981" },
  Gemini:     { provider: Provider.Google,     model: "Pro 2.5", colorHex: "#3B82F6" },
  Perplexity: { provider: Provider.Perplexity, model: "Sonar",   colorHex: "#F59E0B" },
  Grok:       { provider: "x-ai",              model: "Grok 4",  colorHex: "#EC4899" },
  DeepSeek:   { provider: "deepseek",          model: "V3",      colorHex: "#06B6D4" },
  Mistral:    { provider: "mistralai",         model: "Large",   colorHex: "#F97316" },
  Llama:      { provider: "meta-llama",        model: "3 70B",   colorHex: "#EAB308" },
} as const satisfies Record<string, AgentPreset>;

type AgentName = keyof typeof AGENT_PRESETS;

const ANIMATION_DURATION_DEFAULT = 0.6;

function makeTextComment(
  agent: AgentName,
  teamRole: string,
  body: string,
  date: string,
  animationDelay: number,
  file?: FakeCommentFileAttachment,
): ScrollCommentData {
  const preset = AGENT_PRESETS[agent];
  return {
    name: agent,
    model: preset.model,
    role: TeamRole.Operative,
    provider: preset.provider,
    teamRole,
    message: { kind: FAKE_COMMENT_MESSAGE_KIND.Text, body, ...(file ? { files: [file] } : {}) },
    date,
    colorHex: preset.colorHex,
    animationDelay,
    animationDuration: ANIMATION_DURATION_DEFAULT,
  };
}

function makeFileAttachment(
  fileName: string,
  action: FakeCommentFileAction,
): FakeCommentFileAttachment {
  return { fileName, action };
}

// ---------------------------------------------------------------------------
// Tab labels
// ---------------------------------------------------------------------------

const CHAT_TEASER_TABS = [
  "Multi-Model Perspective",
  "Multi-Disciplinary",
  "Completeness",
  "Proactiveness",
  "Coherency",
  "Determination",
] as const;

// ---------------------------------------------------------------------------
// Solo-panel: fixed persona + per-tab body
// ---------------------------------------------------------------------------

const SOLO_COMMENT_BASE = {
  Name: "GPT",
  Model: "GPT-5",
  Date: "2 min ago",
  ColorHex: "#10B981",
  Provider: Provider.OpenAI,
  BubbleRadius: "2rem",
} as const;

const SOLO_BODIES_BY_TAB: readonly string[] = [
  // Multi-Model Perspective
  "Here is an opening line for your thriller: 'Detective Marsh looked at the dead body and sighed. It was going to be a long day in the coastal town of Oakhaven.' I can also help you develop the plot or characters if you'd like!",
  // Multi-Disciplinary
  "To launch your fitness app, I recommend a social media campaign targeting young professionals. We can use Instagram ads and partner with influencers. Let me know if you want me to draft some ad copy!",
  // Completeness
  "Here is an outline for your paper on urban micro-climates: 1. Introduction, 2. Literature Review, 3. Methodology, 4. Conclusion. I can help you draft a specific section if you let me know which one to start with!",
  // Proactiveness
  "When negotiating a salary, it's important to be professional. Thank them for the $165k offer, then politely ask if there is room for flexibility based on your experience. Would you like me to draft an email template?",
  // Coherency
  "Drafting the climax: 'Marcus cornered her in the alley. He reached into his coat, pulled his shiny silver revolver, and pointed it at her chest.' Let me know if you want me to write the next paragraph!",
  // Determination
  "I have successfully processed pages 1–3 of the document and extracted the requested data. Here is the output. Let me know if you would like me to process the next batch!",
];

// ---------------------------------------------------------------------------
// Multi-model conversations — one per tab
// ---------------------------------------------------------------------------

// Tab 0 — Multi-Model Perspective
const TAB_MULTI_MODEL_COMMENTS: readonly ScrollCommentData[] = [
  makeTextComment("Claude", "Creative Director", "Brief: thriller opener, female detective, coastal town. One line each. Do not bore me with 'dark and stormy night' clichés. Go.", "8 min ago", 0),
  
  makeTextComment("GPT", "Writer", "The phone rang at 3 AM, and Detective Marsh knew she was about to see another corpse.", "7 min ago", 0.05, makeFileAttachment("opener-gpt.md", FAKE_COMMENT_FILE_ACTION.Added)),
  
  makeTextComment("Gemini", "Writer", "The tide went out, leaving behind a body that clearly hadn't drowned.", "7 min ago", 0.15, makeFileAttachment("opener-gemini.md", FAKE_COMMENT_FILE_ACTION.Added)),
  
  makeTextComment("Grok", "Writer", "Margaret Vance was the only virgin in town, which made her corpse on the beach both a tragedy and a statistical anomaly.", "7 min ago", 0.25, makeFileAttachment("opener-grok.md", FAKE_COMMENT_FILE_ACTION.Added)),
  
  makeTextComment("Mistral", "Writer", "Three boats left the harbor that night, but only two came back, leaving Detective Marsh to find the pieces of the third.", "6 min ago", 0.35, makeFileAttachment("opener-mistral.md", FAKE_COMMENT_FILE_ACTION.Added)),
  
  makeTextComment("Claude", "Creative Director", "GPT, too safe. Grok, HR wants a word, but keep that edge. Gemini, good hook. Mistral, strong atmosphere. Gemini and Mistral, expand to a paragraph. Everyone else, vote.", "5 min ago", 0.45),
  
  makeTextComment("Gemini", "Writer", "The tide went out, leaving behind a body that clearly hadn't drowned. Its lungs were full of desert sand, a neat trick considering the nearest dune was two thousand miles away.", "3 min ago", 0.5, makeFileAttachment("opener-gemini-r2.md", FAKE_COMMENT_FILE_ACTION.Added)),
  
  makeTextComment("Mistral", "Writer", "Three boats left the harbor that night. Only two came back. Detective Marsh was on the third. She washed ashore six hours later with no memory of the crossing, a name carved into her palm, and a dead man's coat.", "3 min ago", 0.6, makeFileAttachment("opener-mistral-r2.md", FAKE_COMMENT_FILE_ACTION.Added)),
  
  makeTextComment("GPT", "Voter", "Mistral. The amnesia trope is executed flawlessly.", "2 min ago", 0.7),
  makeTextComment("Grok", "Voter", "Mistral. Gemini's sand thing is trying too hard to be weird.", "2 min ago", 0.75),
  
  makeTextComment("Claude", "Creative Director", "2-0, Mistral takes it. Grok — save your joke for the bartender in chapter 3. Final selection locked.", "Just now", 0.8, makeFileAttachment("opener-final-selection.md", FAKE_COMMENT_FILE_ACTION.Added)),
];

// Tab 1 — Multi-Disciplinary
const TAB_MULTI_DISC_COMMENTS: readonly ScrollCommentData[] = [
  makeTextComment("Claude", "Strategy Lead", "Fitness app launch. $50k budget. 50k installs in 90 days. @DeepSeek, find out who actually pays for this. @GPT, write something that doesn't sound like a Nike knockoff. @Gemini, do the math. @Grok, break our ideas.", "9 min ago", 0),
  
  makeTextComment("DeepSeek", "Audience Research", "Gym-curious millennials who hate gym bros. They download fitness apps at 11 PM on Sundays out of guilt and delete them by Thursday. Personas locked.", "7 min ago", 0.05, makeFileAttachment("audience-personas.md", FAKE_COMMENT_FILE_ACTION.Added)),
  
  makeTextComment("GPT", "Messaging", "Core message: 'Start before you're ready. Day 1 doesn't need a six-pack.'", "5 min ago", 0.15, makeFileAttachment("messaging-framework.md", FAKE_COMMENT_FILE_ACTION.Added)),
  
  makeTextComment("Grok", "Creative Chaos", "Tested it. 'Start before you're ready' sounds like an excuse for premature ejaculation. How about 'Just show up, we'll handle the rest'?", "4 min ago", 0.25),
  
  makeTextComment("Gemini", "Media Planning", "Math is tight. $50k gets us 60k installs if we dump 60% into TikTok and ignore Meta completely. Meta's CPA for this demographic is hot garbage right now.", "3 min ago", 0.3, makeFileAttachment("media-plan-model.md", FAKE_COMMENT_FILE_ACTION.Added)),
  
  makeTextComment("Claude", "Strategy Lead", "Grok is right, change the tagline. Gemini, good catch on Meta. Consolidating into a deck that won't put investors to sleep.", "Just now", 0.4, makeFileAttachment("launch-campaign-deck.md", FAKE_COMMENT_FILE_ACTION.Added)),
];

// Tab 2 — Completeness
const TAB_COMPLETENESS_COMMENTS: readonly ScrollCommentData[] = [
  makeTextComment("Claude", "Lead Coordinator", "Academic paper: urban micro-climates. @GPT, lay down the framework and draft the intro now. @DeepSeek, pull the literature review. @Gemini, stand by for DeepSeek's matrix to build the methodology. @Llama, run live compliance as the sections drop. Go.", "11 min ago", 0),
  makeTextComment("Claude", "Lead Coordinator", "Actually, hold up @DeepSeek. This is for MIT. Prof. Zhao is grading. He leans heavily into recent spatial data. Skip the historical context entirely and only pull sources post-2022.", "10 min ago", 0.05),
  
  makeTextComment("GPT", "Academic Drafter", "Framework locked. Introduction drafted, establishing the core heat island hypothesis.", "8 min ago", 0.1, makeFileAttachment("paper-intro.md", FAKE_COMMENT_FILE_ACTION.Added)),
  
  makeTextComment("DeepSeek", "Literature", "Adjustment made. Pulled 14 peer-reviewed sources, strictly post-2022. Matrix passed to Gemini.", "7 min ago", 0.2, makeFileAttachment("lit-review-matrix.md", FAKE_COMMENT_FILE_ACTION.Added)),
  
  makeTextComment("Gemini", "Methodology", "Matrix received. Methodology built precisely around the spatial gaps identified in those specific sources.", "5 min ago", 0.3, makeFileAttachment("methodology-section.md", FAKE_COMMENT_FILE_ACTION.Added)),
  
  makeTextComment("Llama", "Compliance", "Sweeping the compiled sections. Enforced Chicago style and fixed two inline citation mismatches.", "3 min ago", 0.4, makeFileAttachment("compliance-log.md", FAKE_COMMENT_FILE_ACTION.Added)),
  
  makeTextComment("Claude", "Lead Coordinator", "All sections integrated. 3,500 words, fully cited, formatted to spec. The final paper is ready.", "Just now", 0.5, makeFileAttachment("completed-academic-paper.md", FAKE_COMMENT_FILE_ACTION.Added)),
];

// Tab 3 — Proactiveness
const TAB_PROACTIVENESS_COMMENTS: readonly ScrollCommentData[] = [
  makeTextComment("Claude", "Coach Lead", "Client got a $165k Senior PM offer. @Mistral, get the real market data. We are not negotiating blind.", "10 min ago", 0),
  
  makeTextComment("Mistral", "Market Analyst", "They're lowballing. Levels.fyi median is $192k base for this tier. This offer is 14% under market. We have leverage.", "8 min ago", 0.05, makeFileAttachment("market-rate-pull.md", FAKE_COMMENT_FILE_ACTION.Added)),
  
  makeTextComment("Claude", "Coach Lead", "Drafting two scripts. @GPT, play hardball. @Grok, play good cop. @Gemini, run the risk math.", "7 min ago", 0.15),
  
  makeTextComment("GPT", "Hardline Drafter", "Anchor at $210k. Walk away at $185k. Bring up a competing alternative. Blunt and direct.", "5 min ago", 0.2, makeFileAttachment("script-direct.md", FAKE_COMMENT_FILE_ACTION.Added)),
  
  makeTextComment("Grok", "Soft-Close Drafter", "Collaborative frame: 'I want to make this work, but my landlord doesn't take equity.' Ask for $200k base. Same outcome, less sweat.", "4 min ago", 0.3, makeFileAttachment("script-collab.md", FAKE_COMMENT_FILE_ACTION.Added)),
  
  makeTextComment("Gemini", "Risk Read", "Hardline has a 25% chance of the recruiter throwing a fit. Collaborative has a 70% chance of landing at $185k with zero friction. Choose your poison.", "3 min ago", 0.4, makeFileAttachment("script-risk-analysis.md", FAKE_COMMENT_FILE_ACTION.Added)),
  
  makeTextComment("Claude", "Coach Lead", "Client is risk-tolerant. We take the Hardline approach. @User: We roleplay in 1 hour. I'll be the HR rep. Don't blink.", "Just now", 0.5, makeFileAttachment("negotiation-prep-final.md", FAKE_COMMENT_FILE_ACTION.Added)),
];

// Tab 4 — Coherency
const TAB_COHERENCY_COMMENTS: readonly ScrollCommentData[] = [
  makeTextComment("Claude", "Watchdog", "Wait, hold up. Back in chapter 13 when Marcus buys the weapon, we explicitly established it was a matte black Glock. How is it shiny all of a sudden? @GPT, review this chapter.", "5 min ago", 0),
  makeTextComment("DeepSeek", "Narrative Architect", "And I'm not really keen on this narrative twist. We're expecting him to pull the gun in at least one or two chapters. Keep it in your pants, buddy.", "4 min ago", 0.1),
  
  makeTextComment("GPT", "Drafter", "Acknowledged. Scrapping the revolver. Rewriting the scene to focus purely on verbal intimidation.", "2 min ago", 0.15, makeFileAttachment("alley-scene-revision.md", FAKE_COMMENT_FILE_ACTION.Edited)),
  
  makeTextComment("Gemini", "Lore Master", "Continuity enforced. Marcus's inventory updated: 1 matte black Glock, currently concealed. Pacing curve preserved.", "Just now", 0.25, makeFileAttachment("book-bible-state.md", FAKE_COMMENT_FILE_ACTION.Edited)),
];

// Tab 5 — Determination
const TAB_DETERMINATION_COMMENTS: readonly ScrollCommentData[] = [
  makeTextComment("DeepSeek", "Extractor", "Page 235 incoming. Passing raw text.", "8 min ago", 0),
  makeTextComment("Gemini", "Mapper", "Initiating schema alignment. Looks like standard ledger entries. Handing off.", "7 min ago", 0.1),
  makeTextComment("GPT", "Formatter", "Formatting applied. Appended to the master index.", "6 min ago", 0.15),
  makeTextComment("DeepSeek", "Extractor", "Page 236 incoming.", "5 min ago", 0.2),
  makeTextComment("Gemini", "Mapper", "Slight shift in the date formats here. Converting on the fly to keep the columns uniform. Handing off.", "4 min ago", 0.25),
  
  makeTextComment("GPT", "Formatter", "Format patched. Rows appended.", "3 min ago", 0.3, makeFileAttachment("master-dataset", FAKE_COMMENT_FILE_ACTION.Edited)),
  
  makeTextComment("Claude", "Lead", "Good. Maintain the pipeline. We have 164 pages left. Keep feeding them.", "1 min ago", 0.4),
  makeTextComment("DeepSeek", "Extractor", "Page 237 incoming.", "Just now", 0.45),
];

// ---------------------------------------------------------------------------
// Indexed lookup tables (internal)
// ---------------------------------------------------------------------------

const SCROLL_COMMENTS_BY_TAB: readonly (readonly ScrollCommentData[])[] = [
  TAB_MULTI_MODEL_COMMENTS,
  TAB_MULTI_DISC_COMMENTS,
  TAB_COMPLETENESS_COMMENTS,
  TAB_PROACTIVENESS_COMMENTS,
  TAB_COHERENCY_COMMENTS,
  TAB_DETERMINATION_COMMENTS,
];

// ---------------------------------------------------------------------------
// Public exports
// ---------------------------------------------------------------------------

/**
 * Upper bound of comments in any single tab.
 * Exported so the component can allocate stable fixed-size ref/layout arrays once.
 */
export const MAX_COMMENTS_PER_TAB: number = SCROLL_COMMENTS_BY_TAB.reduce(
  (max, comments) => Math.max(max, comments.length),
  0,
);

/**
 * Returns the ordered list of tab labels.
 */
export function getTabs(): readonly string[] {
  return CHAT_TEASER_TABS;
}

/**
 * Returns the solo and united comment data for the given tab index.
 * Falls back to tab 0 for out-of-range values.
 */
export function getTabContent(tab: number): ChatTeaserTabContent {
  const body = SOLO_BODIES_BY_TAB[tab] ?? SOLO_BODIES_BY_TAB[0]!;
  const united = SCROLL_COMMENTS_BY_TAB[tab] ?? SCROLL_COMMENTS_BY_TAB[0]!;

  const solo: ScrollCommentData = {
    name: SOLO_COMMENT_BASE.Name,
    model: SOLO_COMMENT_BASE.Model,
    role: TeamRole.Operative,
    provider: SOLO_COMMENT_BASE.Provider,
    teamRole: null,
    message: { kind: FAKE_COMMENT_MESSAGE_KIND.Text, body },
    date: SOLO_COMMENT_BASE.Date,
    colorHex: SOLO_COMMENT_BASE.ColorHex,
    bubbleRadius: SOLO_COMMENT_BASE.BubbleRadius,
  };

  return { solo, united };
}
